import React, { useContext, useMemo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import round from 'lodash/round';

import RouteWrapper from './RouteWrapper';
import RatingAndRoundsByDayChart from '../components/charts/RatingAndRoundsByDayChart';
import SeasonCalendar from '../components/widgets/SeasonCalendar';
import { DataContext } from '../components/data/DataContext';
import { DataContextType } from '../components/data_types/interfaces';
import { DaySummary, PLACEMENT_ROUNDS, summarizeByDate, withoutPlacement } from '../components/data/period_insights';
import { InsightCard, PeriodNav } from '../components/shared/PeriodComponents';
import { DS, Overline, Panel, numericSx } from '../components/shared/Dashboard';

const longestDailyStreak = (days: DaySummary[]) => {
    let best = 0;
    let current = 0;
    days.forEach((day, i) => {
        current = i > 0 && day.date.diff(days[i - 1].date, 'days') === 1 ? current + 1 : 1;
        best = Math.max(best, current);
    });
    return best;
};

const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
    <Box>
        <Overline>{label}</Overline>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: DS.text, lineHeight: 1.3, ...numericSx }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{sub}</Typography>}
    </Box>
);

const ByDate = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData } = context || {};

    const days = useMemo(() => summarizeByDate(historyData ?? []), [historyData]);

    const rankedDays = useMemo(() => summarizeByDate(withoutPlacement(historyData ?? [])), [historyData]);

    const inPlacement = rankedDays.length === 0 && days.length > 0;
    const placementText = `Your first ${PLACEMENT_ROUNDS} rounds are placement and don't count here.`;

    const played = days.filter(d => d.count > 0);
    const bestDay = maxBy(rankedDays.filter(d => d.totalChange > 0), 'totalChange');
    const worstDay = minBy(rankedDays.filter(d => d.totalChange < 0), 'totalChange');
    const busiestDay = maxBy(played, 'count');
    const spanDays = days.length ? days[days.length - 1].date.diff(days[0].date, 'days') + 1 : 0;
    const totalRounds = played.reduce((sum, d) => sum + d.count, 0);

    return (
        <RouteWrapper>
            <PeriodNav title="Your season calendar" subtitle="Every day you played, and what it did to your rating." />

            <InsightCard overline="Best day" accent={DS.gain} period={bestDay} metric="total" emptyText={inPlacement ? placementText : 'No day with a rating gain yet.'} delay={0.05} />
            <InsightCard overline="Toughest day" accent={DS.loss} period={worstDay} metric="total" emptyText={inPlacement ? placementText : 'No day with a rating loss yet.'} delay={0.1} />
            <InsightCard overline="Busiest day" accent={DS.info} period={busiestDay} metric="count" emptyText="No rounds yet." delay={0.15} />

            <Panel title="Season calendar" subtitle="Colour shows the net rating change for the day; hover a day for details." delay={0.1}>
                <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                    <SeasonCalendar days={played} />
                </Box>
                <Box sx={{
                    mt: 3,
                    pt: 2.5,
                    borderTop: `1px solid ${DS.border}`,
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                    gap: 2,
                }}>
                    <Stat label="Days played" value={played.length} sub={`of ${spanDays} days this season`} />
                    <Stat label="Play rate" value={`${spanDays ? round((played.length / spanDays) * 100) : 0}%`} sub="of days since your first round" />
                    <Stat label="Rounds per day played" value={played.length ? round(totalRounds / played.length, 1) : 0} />
                    <Stat label="Longest daily streak" value={longestDailyStreak(played)} sub="days in a row" />
                </Box>
            </Panel>

            <Panel title="Rating by day" subtitle="Line: rating at the end of each day · Bars: net rating change, empty on days you didn't play" delay={0.15}>
                <RatingAndRoundsByDayChart days={played} />
            </Panel>

            <Grid item xs={12} sx={{ mb: 6 }} />
        </RouteWrapper>
    );
};

export default ByDate;
